import { ShieldAlert } from "lucide-react";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import type { SuspiciousPlayBand } from "@/lib/demo-parser";
import {
  formatSuspiciousPlaySampleStrength,
  getSuspiciousPlayBandMeta,
} from "@/lib/suspicious-play";
import { cn } from "@/lib/utils";

interface SuspiciousPlayBadgeProps {
  score?: number;
  band?: SuspiciousPlayBand;
  label?: string;
  reasons?: string[];
  className?: string;
  onClick?: () => void;
}

export function SuspiciousPlayBadge({
  score,
  band,
  label,
  reasons,
  className,
  onClick,
}: SuspiciousPlayBadgeProps) {
  if (score == null) {
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full border border-white/10 bg-white/4 px-3 py-1 text-xs font-medium text-muted-foreground",
          className
        )}
      >
        No score
      </span>
    );
  }

  const meta = getSuspiciousPlayBandMeta(score, band);
  const content = (
    <>
      <ShieldAlert className={cn("h-3.5 w-3.5", meta.textClass)} />
      <span className="font-mono">{score}</span>
      <span className="uppercase tracking-[0.16em]">{label ?? meta.label}</span>
      {reasons && reasons.length > 0 ? (
        <span
          className="inline-flex"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <InfoTooltip
            label="Suspicious-play score reasons"
            content={
              <div className="space-y-1">
                <div className="font-medium text-white">Main signals</div>
                {reasons.map((reason) => (
                  <div key={reason}>{reason}</div>
                ))}
              </div>
            }
            className="ml-0.5"
            panelClassName="left-auto right-0 translate-x-0"
          />
        </span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        className={cn(
          "inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
          meta.containerClass,
          className
        )}
      >
        {content}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
        meta.containerClass,
        className
      )}
    >
      {content}
    </span>
  );
}

interface SuspiciousPlayScoreCardProps {
  score?: number;
  band?: SuspiciousPlayBand;
  label?: string;
  reasons?: string[];
  sampleStrength?: number;
  methodologyVersion?: string;
  className?: string;
}

export function SuspiciousPlayScoreCard({
  score,
  band,
  label,
  reasons,
  sampleStrength,
  methodologyVersion,
  className,
}: SuspiciousPlayScoreCardProps) {
  if (score == null) {
    return (
      <div
        className={cn(
          "rounded-3xl border border-white/10 bg-white/4 p-5",
          className
        )}
      >
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Suspicious Play
        </p>
        <p className="mt-3 text-lg font-semibold text-white">No score available yet</p>
        <p className="mt-2 text-sm text-fg-secondary">
          The parser has not produced enough player evidence for a suspicious-play
          breakdown.
        </p>
      </div>
    );
  }

  const meta = getSuspiciousPlayBandMeta(score, band);

  return (
    <div
      className={cn(
        "rounded-3xl border p-5 shadow-[0_16px_40px_rgba(0,0,0,0.22)]",
        meta.softClass,
        className
      )}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Suspicious Play
          </p>
          <div className="flex items-end gap-3">
            <span className="font-mono text-5xl font-semibold text-white">{score}</span>
            <div className="pb-1">
              <div className={cn("text-sm font-semibold", meta.textClass)}>
                {label ?? meta.label}
              </div>
              <div className="text-xs text-muted-foreground">
                Band {meta.rangeLabel}
              </div>
            </div>
          </div>
          <p className="max-w-xl text-sm text-fg-secondary">{meta.description}</p>
        </div>

        <div className="space-y-2 text-left md:max-w-xs md:text-right">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Review context
          </div>
          <div className="text-sm text-white">
            {formatSuspiciousPlaySampleStrength(sampleStrength)}
          </div>
          <div className="text-xs text-muted-foreground">
            {methodologyVersion ?? "suspicious-play/v1"}
          </div>
          <div className="text-xs leading-5 text-fg-secondary">
            Heuristic signal only. This is not proof of cheating.
          </div>
        </div>
      </div>

      {reasons && reasons.length > 0 ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {reasons.map((reason) => (
            <span
              key={reason}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-foreground/90"
            >
              {reason}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
